import { useContext } from "react";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";

// UI hints only: every permission here is enforced again by the backend
// (utils/access/menuAccess.js). The role -> permission mapping lives in
// src/access/modules/menu.js; this hook only reshapes it for the menu screens.
export function useMenuRole() {
  const { can, has } = useAccess();
  const { branchAccess } = useContext(Context) || {};
  const isLud = can("menu.audience.lud");
  const isRestaurantManager = can("menu.audience.restaurantManager");
  const isBranchUser = can("menu.audience.branchUser");
  const isProspect = can("menu.audience.prospect");
  return {
    side: isLud ? "lud" : isRestaurantManager || isBranchUser || isProspect ? "restaurant" : null,
    isLud,
    isRestaurantManager,
    isProspect,
    isBranchUser,
    // dm is read-only on menus; branch users only toggle their branches.
    canWrite: has("menu.manage"),
    canBranchVisibility: has("menu.branch.visibility"),
    canViewAdditions: can("menu.addition.view"),
    assignedBranches: Array.isArray(branchAccess) ? branchAccess.map(String) : [],
  };
}

export const LIFECYCLE_LABELS = {
  ar: { draft: "مسودة", active: "مفعّلة", archived: "مؤرشفة" },
  en: { draft: "Draft", active: "Active", archived: "Archived" },
};

export const localized = (value, lang) => value?.[lang] || value?.ar || value?.en || "";

// Friendly text for backend error codes.
export function menuErrorMessage(error, lang = "ar") {
  const ar = lang === "ar";
  const messages = {
    MENU_NAME_TAKEN: ar ? "توجد قائمة طعام أخرى بنفس الاسم لهذا المطعم." : "Another menu of this restaurant already uses this name.",
    MENU_NOT_ACTIVE: ar ? "هذا الإجراء متاح لقوائم الطعام المفعّلة فقط." : "This is only available for active menus.",
    MENU_ARCHIVED: ar ? "قائمة الطعام مؤرشفة ولا يمكن تعديلها." : "Archived menus cannot be changed.",
    ADDITION_IN_USE: ar ? "هذه الإضافة مرتبطة بأصناف. افصلها عنها أولاً." : "This addition is linked to items. Unlink it first.",
    SHORT_NAME_TAKEN: ar ? "الاسم المختصر مستخدم لإضافة أخرى في هذا المطعم." : "This short name is used by another addition of this restaurant.",
    BUSY: ar ? "يتم تعديل قائمة الطعام من شخص آخر الآن، حاول مرة أخرى." : "Someone else is changing this menu right now, try again.",
    MENU_NOT_DRAFT: ar ? "هذا الإجراء متاح للمسودات فقط." : "This is only available for draft menus.",
    MENU_NOT_ARCHIVED: ar ? "قائمة الطعام ليست مؤرشفة." : "This menu is not archived.",
    NOT_ARCHIVED: ar ? "العنصر ليس مؤرشفاً." : "This is not archived.",
    SECTION_ARCHIVED: ar ? "استعد القسم أولاً ثم الصنف." : "Restore the section first, then the item.",
    BRANCH_NOT_ASSIGNED: ar ? "هذا الفرع غير مرتبط بقائمة الطعام هذه." : "This branch does not use this menu.",
    ADDITION_ARCHIVED: ar ? "الإضافة مؤرشفة. استعدها أولاً." : "This addition is archived. Restore it first.",
    ADDITION_NOT_ARCHIVED: ar ? "الإضافة ليست مؤرشفة." : "This addition is not archived.",
  };
  if (messages[error?.code]) return messages[error.code];
  if (error?.status === 403) return ar ? "ليست لديك صلاحية لهذا الإجراء." : "You do not have permission to do this.";
  if (error?.status === 404) return ar ? "العنصر غير موجود أو لا تملك صلاحية الوصول إليه." : "Not found, or you do not have access to it.";
  return error?.message || (ar ? "حدث خطأ" : "Something went wrong");
}
